import axios from 'axios';
import toast from 'react-hot-toast';
import { Capacitor } from '@capacitor/core';
import { cachedRequest, invalidateCache } from '../utils/apiCache';
import i18n from '../i18n';

// Get the base URL for API calls
const getBaseURL = () => {
  // Native Capacitor app must use absolute URL (WebView is at capacitor://localhost)
  if (Capacitor.isNativePlatform()) {
    return 'https://protrener.app/api';
  }
  
  // FORCE relative URLs in production to avoid CORS issues
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
  
  // Development fallback - only use REACT_APP_API_URL in development
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  return 'http://localhost:5001/api';
};

// Create axios instance
const baseURL = getBaseURL();

const api = axios.create({
  baseURL: baseURL,
  timeout: 120000, // 120 seconds (2 minutes) for video uploads
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper: origin for static files (strip trailing /api if present)
export const API_ORIGIN = (() => {
  // Native Capacitor app: always use production origin
  if (Capacitor.isNativePlatform()) {
    return 'https://protrener.app';
  }
  
  const base = getBaseURL();
  
  // If it's a relative URL (like '/api'), use current window origin
  if (base.startsWith('/')) {
    return typeof window !== 'undefined' ? window.location.origin : '';
  }
  
  try {
    const url = new URL(base);
    // Just return the origin (protocol + host + port) for static files
    return url.origin;
  } catch {
    // Fallback if base is not a valid URL
    return process.env.NODE_ENV === 'production' && typeof window !== 'undefined' 
      ? window.location.origin 
      : 'http://localhost:5001';
  }
})();

// Request interceptor to add auth token and handle Content-Type
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Remember WHICH session issued this request and WHEN. Responses can land
    // long after the session that sent them is gone (slow mobile network, an
    // iOS WebView resuming a burst of stalled requests, a logout+login in
    // between). Both stamps let the response interceptor tell "this belongs to
    // the session I'm in now" from "this is debris from a dead session".
    config._sentWithToken = token || null;
    config._sentAt = Date.now();

    // Let axios set Content-Type automatically for FormData (multipart/form-data)
    // This is needed for file uploads
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// --- Session-safety helpers for the sliding-refresh / kick logic ---------------
// Decode a JWT payload WITHOUT verifying it. The signature is the server's job;
// here we only need the advisory claims (id/role/iat/exp) to decide whether a
// token belongs to the session we are currently in. Returns null on anything
// that is not a well-formed JWT.
const decodeJwtPayload = (token) => {
  try {
    const part = String(token).split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' ? payload : null;
  } catch (_) {
    return null;
  }
};

/**
 * Decide whether an X-Refreshed-Token may replace the stored token.
 *
 * The refresh header is minted from whatever token the REQUEST carried, so a
 * response from a dead session (in-flight across a logout/login, a resumed
 * WebView request, a replayed cached response) would otherwise silently
 * overwrite the token the user just logged in with — and the very next request
 * then 401/403s and the interceptor kicks the user back to /login.
 *
 * Accept only when ALL of these hold:
 *   - we still have a stored token (never resurrect a logged-out session),
 *   - the request was sent with exactly that stored token (same session),
 *   - both tokens decode and describe the SAME identity (id + role),
 *   - the new token is not expired and not older than the one we hold.
 *
 * Exported for testing / reuse; behaviour on the happy path is unchanged.
 */
export const shouldAcceptRefreshedToken = (refreshed, sentWithToken, currentToken, now = Date.now()) => {
  if (!refreshed || typeof refreshed !== 'string') return false;
  if (!currentToken) return false;
  // A request sent with a different token than the one stored now belongs to a
  // superseded session — its refresh must not clobber the current one.
  if (sentWithToken !== currentToken) return false;

  const next = decodeJwtPayload(refreshed);
  const current = decodeJwtPayload(currentToken);
  if (!next || !current) return false;

  // Never swap the logged-in identity out from under the UI.
  if (next.id !== current.id || next.role !== current.role) return false;

  // Must be usable, and never a step backwards (kills replays of stale headers).
  if (typeof next.exp !== 'number' || next.exp * 1000 <= now) return false;
  if (
    typeof next.iat === 'number' &&
    typeof current.iat === 'number' &&
    next.iat < current.iat
  ) {
    return false;
  }
  return true;
};

/**
 * True when the failed request was issued BEFORE the token we currently hold was
 * minted — i.e. it is debris from a previous session and its 401/403 says
 * nothing about the session the user is in now. `iat` is floored to whole
 * seconds, which only ever makes this check more conservative (it can under-
 * report staleness, never over-report it), so a genuine failure is still kicked.
 */
export const isStaleSessionError = (config) => {
  try {
    const sentAt = config?._sentAt;
    if (typeof sentAt !== 'number') return false;
    const current = localStorage.getItem('token');
    if (!current) return false;
    // Same token still stored → same session → the error is genuine.
    if (config._sentWithToken === current) return false;
    const payload = decodeJwtPayload(current);
    if (!payload || typeof payload.iat !== 'number') return false;
    return payload.iat * 1000 > sentAt;
  } catch (_) {
    return false;
  }
};

// Active workout session flag - prevents 401/403 redirect during workouts
// so the workout page can handle retry/re-auth without losing data
let _activeWorkoutSession = false;
export const setActiveWorkout = (active) => { _activeWorkoutSession = active; };
export const isActiveWorkout = () => _activeWorkoutSession;

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Sliding-session support: the backend may mint a fresh JWT on any authed
    // response and return it via the X-Refreshed-Token header. Pick it up
    // silently so a member's token never expires mid-workout — but ONLY when it
    // provably belongs to the session we are in right now (see
    // shouldAcceptRefreshedToken). An unconditional write lets a response from a
    // dead session overwrite a just-issued login token, which kicks the user
    // straight back to /login on the next request.
    try {
      const refreshed = response.headers?.['x-refreshed-token'];
      if (
        refreshed &&
        shouldAcceptRefreshedToken(
          refreshed,
          response.config?._sentWithToken ?? null,
          localStorage.getItem('token')
        )
      ) {
        localStorage.setItem('token', refreshed);
      }
    } catch (_) {
      // localStorage unavailable — ignore, token stays as-is
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 503 && error.response?.data?.maintenance) {
      const lang = i18n.language || 'hr';
      const isEnglish = lang.toLowerCase().startsWith('en');
      const message = (isEnglish
        ? error.response.data.messageEn
        : error.response.data.message
      ) || error.response.data.message
        || error.response.data.messageEn
        || (isEnglish
          ? 'Application is under maintenance. Please try again in an hour.'
          : 'Aplikacija je u održavanju. Pokušajte ponovo za sat vremena.');
      toast.error(message, { id: 'maintenance', duration: 10000 });
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      // If a workout is active, do NOT redirect -- let the workout page handle it
      if (_activeWorkoutSession) {
        return Promise.reject(error);
      }
      // Debris from a previous session (sent before the current token existed)
      // must never log out the session the user just logged into.
      if (isStaleSessionError(error.config)) {
        return Promise.reject(error);
      }
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      // If a workout is active, do NOT redirect
      if (_activeWorkoutSession) {
        return Promise.reject(error);
      }
      if (isStaleSessionError(error.config)) {
        return Promise.reject(error);
      }

      // Only treat as "session outdated" if the error is from the auth middleware
      // (role mismatch), NOT from application-level access checks
      const errorMsg = error.response?.data?.message || '';
      const isRoleMismatch = errorMsg === 'Insufficient permissions for this action';
      
      if (isRoleMismatch) {
        // Show user-friendly message
        const message = 'Your session is outdated. Please login again to refresh your permissions.';
        if (typeof window !== 'undefined' && window.alert) {
          window.alert(message);
        }
        
        // Clear auth data and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      // For other 403s (product access denied, etc.), just let them propagate
      // to the calling code's catch block
    }
    return Promise.reject(error);
  }
);

// Helper function for cached GET requests
export const cachedGet = (url, options = {}) => {
  // Extract axios-specific options
  const { params, cache = true, ttl, deduplicate = true, ...axiosOptions } = options;
  
  return cachedRequest(
    () => api.get(url, { params, ...axiosOptions }),
    {
      method: 'GET',
      url,
      params: params,
      cache: cache,
      ttl: ttl,
      deduplicate: deduplicate
    }
  );
};

// Helper to invalidate cache after mutations
export const invalidateApiCache = invalidateCache;

// Auth API
export const authAPI = {
  login: (email, password) => 
    api.post('/auth/login', { email, password }),
  
  register: (userType, userData) => 
    api.post(`/auth/register/${userType}`, userData),
  
  verifyToken: () => 
    api.post('/auth/verify'),

  registerDeviceToken: (data) =>
    api.post('/auth/device-token', data),

  unregisterDeviceToken: (data) =>
    api.delete('/auth/device-token', { data }),

  updateLanguage: (language) =>
    api.put('/auth/language', { language }),
};

// Admin API
export const adminAPI = {
  // Dashboard
  getDashboardStats: () =>
    api.get('/admin/dashboard'),
  
  // Users
  createUser: (userData) =>
    api.post('/admin/create-user', userData),
  
  // Admin management endpoints
  createAdmin: (adminData) =>
    api.post('/admin/create-admin', adminData),
  
  changeUserPassword: (userId, newPassword, userType) =>
    api.put(`/admin/change-password/${userId}?userType=${userType}`, { newPassword }),
  
  resetUserPassword: (userType, userId, newPassword) =>
    api.put(`/admin/reset-password/${userType}/${userId}`, { newPassword }),
  
  deleteUser: (userId, userType) =>
    api.delete(`/admin/delete-user/${userId}?userType=${userType}`),
  
  getAllUsers: () =>
    api.get('/admin/all-users'),
  
  getAdminStatistics: () =>
    api.get('/admin/statistics'),
  
  getTrainers: (params = {}) =>
    api.get('/admin/trainers', { params }),
  
  getMembers: (params = {}) =>
    api.get('/admin/members', { params }),
  
  getTrainersList: () =>
    api.get('/admin/trainers-list'),
  
  // User management
  updateTrainerPaymentStatus: (trainerId, paymentStatus) =>
    api.put(`/admin/trainers/${trainerId}/payment-status`, { paymentStatus }),
  
  deleteTrainer: (trainerId) =>
    api.delete(`/admin/trainers/${trainerId}`),
  
  deleteMember: (memberId) =>
    api.delete(`/admin/members/${memberId}`),

  assignMemberToTrainer: (memberId, trainerId) =>
    api.put(`/admin/members/${memberId}/assign-trainer`, { trainerId }),

  toggleMemberStatus: (memberId, isActive) =>
    api.put(`/admin/members/${memberId}/toggle-status`, { isActive }),

  // Trainer management
  resetTrainerPassword: (trainerId, { newPassword }) =>
    api.put(`/admin/trainers/${trainerId}/password`, { newPassword }),

  updateTrainerSubscription: (trainerId, { subscriptionPlanId }) =>
    api.put(`/admin/trainers/${trainerId}/subscription`, { subscriptionPlanId }),

  approveTrainer: (trainerId) =>
    api.put(`/admin/trainers/${trainerId}/approve`),

  rejectTrainer: (trainerId) =>
    api.put(`/admin/trainers/${trainerId}/reject`),

  // Subscription plans
  getPlans: () => api.get('/admin/plans'),
  createPlan: (planData) => api.post('/admin/plans', planData),
  updatePlan: (planId, planData) => api.put(`/admin/plans/${planId}`, planData),
  deletePlan: (planId) => api.delete(`/admin/plans/${planId}`),

  // Subscription payments (cash)
  getSubscriptionPayments: (params = {}) => api.get('/admin/subscription-payments', { params }),
  createSubscriptionPayment: (paymentData) => api.post('/admin/subscription-payments', paymentData),
  approveSubscriptionPayment: (paymentId) => api.put(`/admin/subscription-payments/${paymentId}/approve`),
  declineSubscriptionPayment: (paymentId) => api.put(`/admin/subscription-payments/${paymentId}/decline`),

  // Analytics
  getAnalytics: () => api.get('/admin/analytics'),

  // Trainer Membership Management
  updateTrainerMembership: (trainerId, membershipData) =>
    api.put(`/admin/trainers/${trainerId}/membership`, membershipData),
  
  // Payment Statistics
  getTrainerPaymentStats: (trainerId, params = {}) =>
    api.get(`/admin/trainers/${trainerId}/payment-stats`, { params }),
  
  getMemberPaymentStats: (memberId, params = {}) =>
    api.get(`/admin/members/${memberId}/payment-stats`, { params }),
  
  getPaymentAnalytics: (params = {}) =>
    api.get('/admin/payment-analytics', { params }),

  // Registration Fee Settings (Members)
  getRegistrationSettings: () => api.get('/admin/registration-settings'),
  getRegistrationSettingsPublic: () => api.get('/admin/registration-settings/public'),
  updateRegistrationSettings: (settings) => api.put('/admin/registration-settings', settings),
  getPendingRegistrationPayments: () => api.get('/admin/pending-registration-payments'),
  confirmRegistrationPayment: (memberId) => api.put(`/admin/confirm-registration-payment/${memberId}`),

  // Trainer Registration Fee Settings
  getTrainerRegistrationSettings: () => api.get('/admin/trainer-registration-settings'),
  getTrainerRegistrationSettingsPublic: () => api.get('/admin/trainer-registration-settings/public'),
  updateTrainerRegistrationSettings: (settings) => api.put('/admin/trainer-registration-settings', settings),
  getPendingTrainerRegistrationPayments: () => api.get('/admin/pending-trainer-registration-payments'),

  // Social Links Settings
  getSocialLinks: () => api.get('/admin/social-links'),
  getSocialLinksPublic: () => api.get('/public/social-links'),
  updateSocialLinks: (settings) => api.put('/admin/social-links', settings),
};

// Exercise API
export const exerciseAPI = {
  // Get exercises (role-based) - Cached for 10 minutes (static content)
  getExercises: (params = {}) =>
    cachedGet('/exercises', { 
      params,
      ttl: 10 * 60 * 1000 // 10 minutes
    }),
  
  // Get ALL the trainer's exercises as a lightweight [{ id, name }] list for
  // pickers/selects (no pagination cap) - Cached for 10 minutes
  getAllForPicker: () =>
    cachedGet('/exercises/all', {
      ttl: 10 * 60 * 1000 // 10 minutes
    }),

  // Get single exercise - Cached for 10 minutes
  getExercise: (exerciseId) =>
    cachedGet(`/exercises/${exerciseId}`, {
      ttl: 10 * 60 * 1000 // 10 minutes
    }),
  
  // Create exercise (trainers only) - Invalidate cache after
  createExercise: async (exerciseData) => {
    const response = await api.post('/exercises', exerciseData);
    invalidateApiCache(['/exercises']);
    return response;
  },
  
  // Update exercise (trainers only) - Invalidate cache after
  updateExercise: async (exerciseId, exerciseData) => {
    const response = await api.put(`/exercises/${exerciseId}`, exerciseData);
    invalidateApiCache(['/exercises', `/exercises/${exerciseId}`]);
    return response;
  },
  
  // Delete exercise (trainers only) - Invalidate cache after
  deleteExercise: async (exerciseId) => {
    const response = await api.delete(`/exercises/${exerciseId}`);
    invalidateApiCache(['/exercises', `/exercises/${exerciseId}`]);
    return response;
  },

  // Upload media file for exercise
  uploadMedia: (formData) =>
    api.post('/upload/exercise-media', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 180000 // 3 minutes for large video uploads
    }),
  
  // Delete uploaded media file
  deleteMedia: (filename) =>
    api.delete(`/upload/exercise-media/${filename}`)
};

// Category API
export const categoryAPI = {
  // Get trainer's categories - Cached for 5 minutes
  getCategories: (params = {}) =>
    cachedGet('/categories', {
      params,
      ttl: 5 * 60 * 1000 // 5 minutes
    }),
  
  // Create category (trainers only) - Invalidate cache after
  createCategory: async (categoryData) => {
    const response = await api.post('/categories', categoryData);
    invalidateApiCache(['/categories']);
    return response;
  },
  
  // Update category (trainers only) - Invalidate cache after
  updateCategory: async (categoryId, categoryData) => {
    const response = await api.put(`/categories/${categoryId}`, categoryData);
    invalidateApiCache(['/categories']);
    return response;
  },
  
  // Delete category (trainers only) - Invalidate cache after
  deleteCategory: async (categoryId) => {
    const response = await api.delete(`/categories/${categoryId}`);
    invalidateApiCache(['/categories']);
    return response;
  },

  // Upload category image
  uploadImage: (formData) =>
    api.post('/upload/category-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 60000 // 1 minute
    })
};

// Workout Category API
export const workoutCategoryAPI = {
  // Get trainer's workout categories - Cached for 5 minutes
  getCategories: (params = {}) =>
    cachedGet('/workout-categories', {
      params,
      ttl: 5 * 60 * 1000 // 5 minutes
    }),
  
  // Create workout category (trainers only) - Invalidate cache after
  createCategory: async (categoryData) => {
    const response = await api.post('/workout-categories', categoryData);
    invalidateApiCache(['/workout-categories', '/plans']);
    return response;
  },
  
  // Update workout category (trainers only) - Invalidate cache after
  updateCategory: async (categoryId, categoryData) => {
    const response = await api.put(`/workout-categories/${categoryId}`, categoryData);
    invalidateApiCache(['/workout-categories', '/plans']);
    return response;
  },
  
  // Delete workout category (trainers only) - Invalidate cache after
  deleteCategory: async (categoryId) => {
    const response = await api.delete(`/workout-categories/${categoryId}`);
    invalidateApiCache(['/workout-categories', '/plans']);
    return response;
  },

  // Upload category image (reuse category image upload endpoint)
  uploadImage: (formData) =>
    api.post('/upload/category-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 60000 // 1 minute
    })
};

// Trainer API
export const trainerAPI = {
  // Get trainer dashboard stats
  getDashboardStats: () =>
    api.get('/trainer/dashboard'),
  
  // Get trainer's members
  getMembers: (params = {}) =>
    api.get('/trainer/members', { params }),
  
  // Get single member details
  getMember: (memberId) =>
    api.get(`/trainer/members/${memberId}`),
  
  // Add new member to trainer
  addMember: (memberData) =>
    api.post('/trainer/members', memberData),
  
  // Update member information
  updateMember: (memberId, memberData) =>
    api.put(`/trainer/members/${memberId}`, memberData),
  
  // Reset member password
  resetMemberPassword: (memberId, passwordData) =>
    api.put(`/trainer/members/${memberId}/password`, passwordData),
  
  // Update member membership
  updateMemberMembership: (memberId, membershipData) =>
    api.put(`/trainer/members/${memberId}/membership`, membershipData),
  
  // Update member status (active/inactive)
  updateMemberStatus: (memberId, statusData) =>
    api.put(`/trainer/members/${memberId}/status`, statusData),
  
  // Remove member from trainer
  removeMember: (memberId) =>
    api.delete(`/trainer/members/${memberId}`),
  
  // Delete member (alias for removeMember)
  deleteMember: (memberId) =>
    api.delete(`/trainer/members/${memberId}`),
  
  // Get trainer's sessions
  getSessions: () =>
    api.get('/trainer/sessions'),
  
  // Create training session for a member
  createSession: (sessionData) =>
    api.post('/sessions', sessionData),
  
  // Get member payment statistics (for trainer's products only)
  getMemberPaymentStats: (memberId, params = {}) =>
    api.get(`/trainer/members/${memberId}/payment-stats`, { params }),
  
  getMemberProductAccess: (memberId) =>
    api.get(`/trainer/members/${memberId}/product-access`),
  
  extendMemberProductAccess: (memberId, accessId, additionalDays) =>
    api.put(`/trainer/members/${memberId}/product-access/${accessId}/extend`, { additionalDays }),
  
  // Membership Plans (Subscriptions for Members)
  getMembershipPlans: () =>
    api.get('/trainer/membership-plans'),
  
  getMembershipPlan: (planId) =>
    api.get(`/trainer/membership-plans/${planId}`),
  
  createMembershipPlan: (planData) =>
    api.post('/trainer/membership-plans', planData),
  
  updateMembershipPlan: (planId, planData) =>
    api.put(`/trainer/membership-plans/${planId}`, planData),
  
  deleteMembershipPlan: (planId) =>
    api.delete(`/trainer/membership-plans/${planId}`),
  
  // Member Subscriptions
  getMemberSubscriptions: (params = {}) =>
    api.get('/trainer/member-subscriptions', { params }),
  
  confirmCashSubscription: (subscriptionId) =>
    api.post(`/trainer/member-subscriptions/${subscriptionId}/confirm-cash`),

  // Trainer Registration Fee Payment
  getRegistrationFeeStatus: () =>
    api.get('/trainer/registration-fee-status'),
  
  payRegistrationFee: () =>
    api.post('/trainer/pay-registration-fee'),

  confirmRegistrationPayment: (sessionId) =>
    api.post('/trainer/confirm-registration-payment', { sessionId }),

  // WhatsApp contact number (powers the shop FAB)
  getWhatsapp: () => api.get('/trainer/whatsapp'),
  updateWhatsapp: (data) => api.put('/trainer/whatsapp', data),
};

// Trainer Challenges API — maps to /api/trainer/challenges (Phase 2b backend).
// Auth header is attached by the shared axios instance.
export const challengeAPI = {
  // List the trainer's challenges (joined to backing product + participant counts)
  // opts.archived === true -> only archived; otherwise the default (non-archived) list
  getTrainerChallenges: (opts = {}) =>
    api.get(`/trainer/challenges${opts.archived ? '?archived=true' : ''}`),

  // Create a challenge (creates backing ShopProduct + Challenge in one transaction)
  createChallenge: (body) =>
    api.post('/trainer/challenges', body),

  // Update a challenge (partial — only provided fields)
  updateChallenge: (id, body) =>
    api.put(`/trainer/challenges/${id}`, body),

  // Soft-archive a challenge (deactivates backing product)
  deleteChallenge: (id) =>
    api.delete(`/trainer/challenges/${id}`),

  // Invite one or more members via a free grant of the backing product
  inviteToChallenge: (id, memberIds) =>
    api.post(`/trainer/challenges/${id}/invite`, { memberIds }),

  // Aggregate stats for a challenge the trainer owns (participants/completion/avg). - Read view, short cache
  getStats: (id) =>
    cachedGet(`/trainer/challenges/${id}/stats`, {
      ttl: 30 * 1000 // 30 seconds
    }),
};

// Trainer Meal Plans API — maps to /api/trainer/meal-plans (structured nutrition plans).
// Product-backed entity like challenges: create/update wipe-and-rewrite the full
// day → meal → item tree. List + single are short-cached; every mutation invalidates them
// (mirrors programAPI). Auth header is attached by the shared axios instance.
export const mealPlanAPI = {
  // List the trainer's meal plans (joined to backing product + day/assignment counts).
  // opts.archived === true -> only archived; otherwise the default (non-archived) list.
  getAll: (opts = {}) =>
    cachedGet('/trainer/meal-plans', {
      params: opts.archived ? { archived: 'true' } : {},
      ttl: 30 * 1000 // 30 seconds
    }),

  // Full plan tree (days -> meals -> items) + backing product (for edit/duplicate).
  getOne: (id) =>
    cachedGet(`/trainer/meal-plans/${id}`, {
      ttl: 30 * 1000 // 30 seconds
    }),

  // Create a meal plan (backing ShopProduct + MealPlan tree in one transaction).
  create: async (body) => {
    const response = await api.post('/trainer/meal-plans', body);
    invalidateApiCache(['/trainer/meal-plans']);
    return response;
  },

  // Update product fields + wipe-and-rewrite the tree.
  update: async (id, body) => {
    const response = await api.put(`/trainer/meal-plans/${id}`, body);
    invalidateApiCache(['/trainer/meal-plans']);
    return response;
  },

  // Soft-archive a meal plan (deactivates the backing product).
  archive: async (id) => {
    const response = await api.delete(`/trainer/meal-plans/${id}`);
    invalidateApiCache(['/trainer/meal-plans']);
    return response;
  },

  // Invite one or more members via a free grant of the backing product.
  invite: (id, memberIds) =>
    api.post(`/trainer/meal-plans/${id}/invite`, { memberIds }),

  // Personal food-library auto-suggest (prefix search; per-100g macros). Always fresh
  // so newly saved foods are suggested immediately. Returns { items: [...] }.
  foodLibrarySearch: (q) =>
    api.get('/trainer/meal-plans/food-library', { params: { q } }),

  // Full personal food library (alphabetical) for the management UI. Returns { items: [...] }.
  foodLibraryList: () =>
    api.get('/trainer/meal-plans/food-library'),

  // Create/update a food-library entry by name (upsert on [trainerId, name]).
  // body: { name, nameEn?, nameDe?, per100gKcal, per100gProteinG, per100gCarbsG, per100gFatG }.
  // Returns { item }.
  foodLibrarySave: (body) =>
    api.post('/trainer/meal-plans/food-library', body),

  // Delete a food-library entry (own entries only). Returns { success: true }.
  foodLibraryDelete: (id) =>
    api.delete(`/trainer/meal-plans/food-library/${id}`),

  // Per-member adherence rollup for a plan the trainer owns (checked/total last 7/30d).
  // Trainer stats UI arrives in a later wave — the method is wired now.
  adherence: (id) =>
    api.get(`/trainer/meal-plans/${id}/adherence`),
};

// Blog API (trainer-side authoring). No aggressive caching: the list is read
// straight from the server and refetched after every mutation so the manager
// always reflects the latest drafts/published state.
export const blogAPI = {
  // List the authenticated trainer's own posts (drafts + published).
  getMyPosts: () =>
    api.get('/blog/trainer/posts'),

  // Fetch a single own post for editing (full contentBlocks payload).
  getPost: (id) =>
    api.get(`/blog/trainer/posts/${id}`),

  // Create a post. body: { title, excerpt?, coverImageUrl?, contentBlocks,
  // isPublished?, visibility?('public'|'members'), isFeatured? }
  createPost: (body) =>
    api.post('/blog/trainer/posts', body),

  // Update an own post (ownership-enforced server-side).
  updatePost: (id, body) =>
    api.put(`/blog/trainer/posts/${id}`, body),

  // Delete an own post.
  deletePost: (id) =>
    api.delete(`/blog/trainer/posts/${id}`),

  // --- Public blog (no auth required). The shared `api` instance still attaches
  // the auth token when present, so a logged-in member automatically sees
  // members-only posts while a guest sees only public ones. ---

  // List published posts (visibility-filtered server-side). Returns { posts: [...] }.
  getPublicPosts: () =>
    api.get('/blog/public/posts'),

  // Fetch a single published post by slug with resolved embeds.
  // Returns { post, embeds }. 404 for missing/unpublished/members-only-as-guest.
  getPublicPost: (slug) =>
    api.get(`/blog/public/posts/${slug}`),
};

// Member API
export const memberAPI = {
  // Get member dashboard - Cached for 30 seconds (frequently changing)
  getDashboard: () =>
    cachedGet('/member/dashboard', {
      ttl: 30 * 1000 // 30 seconds
    }),
  
  // Update member profile - Invalidate dashboard cache
  updateProfile: async (profileData) => {
    const response = await api.put('/member/profile', profileData);
    invalidateApiCache(['/member/dashboard']);
    return response;
  },
  
  // Get member sessions - Cached for 30 seconds (frequently changing data)
  getSessions: (options = {}) => {
    const includeRecent = options.includeRecent !== undefined ? options.includeRecent : true;
    return cachedGet('/member/sessions', {
      params: { includeRecent: includeRecent ? 'true' : 'false' },
      ttl: 30 * 1000 // 30 seconds (reduced from 2 minutes)
    });
  },
  
  // Get member progress photos - Cached for 5 minutes
  getProgressPhotos: () =>
    cachedGet('/member/progress-photos', {
      ttl: 5 * 60 * 1000 // 5 minutes
    }),
  
  // Get training hub data - Cached for 30 seconds
  getTrainingHub: () =>
    cachedGet('/member/training-hub', {
      ttl: 30 * 1000 // 30 seconds
    }),
  
  // Repeat a completed workout (creates new session from same plan)
  repeatWorkout: async (sessionId, workoutType) => {
    const response = await api.post('/member/repeat-workout', { sessionId, workoutType });
    invalidateApiCache(['/member/sessions', '/member/dashboard', '/member/training-hub']);
    return response;
  },

  // Registration fee payment methods
  getRegistrationFeeStatus: () =>
    api.get('/member/registration-fee-status'),
  
  payRegistrationFee: () =>
    api.post('/member/pay-registration-fee'),
  
  confirmRegistrationPayment: (sessionId) =>
    api.post('/member/confirm-registration-payment', { sessionId }),
  
  // Subscription methods
  getAvailablePlans: () =>
    api.get('/member/available-plans'),
  
  subscribeStripe: (planId, interval) =>
    api.post('/member/subscribe/stripe', { planId, interval }),
  
  subscribeCash: (planId, interval) =>
    api.post('/member/subscribe/cash', { planId, interval }),
  
  getMySubscription: () =>
    api.get('/member/my-subscription'),
  
  cancelSubscription: (subscriptionId) =>
    api.post('/member/subscription/cancel', { subscriptionId }),
  
  // Get recent purchases (last 30 days) for dashboard display - Cached for 1 minute
  getRecentPurchases: () =>
    cachedGet('/member/recent-purchases', {
      ttl: 60 * 1000 // 1 minute
    }),

  // Get the member's trainer's active challenges (+ own participation) - Cached for 1 minute
  getChallenges: () =>
    cachedGet('/member/challenges', {
      ttl: 60 * 1000 // 1 minute
    }),

  // Join a FREE challenge. Throws 402 { requiresPurchase: true } for paid challenges. - Mutation
  joinChallenge: async (challengeId) => {
    const response = await api.post(`/member/challenges/${challengeId}/join`);
    invalidateApiCache(['/member/challenges']);
    return response;
  },

  // Start the linked content for an active challenge participant. - Mutation
  // Pass { planId } to start a specific attached workout Plan (planId must be in the
  // challenge's linkedPlanIds) → { sessionId } (challenge-tagged → completion advances
  // progress). Called with no options for the legacy program path.
  // Returns one of: { sessionId } | { programId } | { noContent: true }.
  startChallenge: async (challengeId, { planId } = {}) => {
    const body = planId ? { planId } : undefined;
    const response = await api.post(`/member/challenges/${challengeId}/start`, body);
    invalidateApiCache(['/member/challenges']);
    return response;
  },

  // Save the member's starting 1RMs for an interactive (rsr) challenge. - Mutation
  // body: { squat1RM, bench1RM, deadlift1RM } (kg). Returns { success, inputData }.
  saveChallengeInputs: async (challengeId, inputs) => {
    const response = await api.post(`/member/challenges/${challengeId}/inputs`, inputs);
    invalidateApiCache(['/member/challenges']);
    return response;
  },

  // Personal stats for a joined challenge (sessions/volume/active days/progress). - Read view, short cache
  // Gated: member must be a participant.
  getChallengeStats: (challengeId) =>
    cachedGet(`/member/challenges/${challengeId}/stats`, {
      ttl: 30 * 1000 // 30 seconds
    }),

  // RSR program tracker: per-week/day prescribed lifts + each day's session status + stats. - Read view, short cache
  // Returns { inputData, weeks:[{ week, days:[{ day, prescribed:[...], session }] }], stats }.
  getRsrProgram: (challengeId) =>
    cachedGet(`/member/challenges/${challengeId}/rsr`, {
      ttl: 15 * 1000 // 15 seconds (statuses change after a workout)
    }),

  // Start (or resume) a loggable RSR session for a given week/day. Idempotent. - Mutation
  // body: { week, day }. Returns { sessionId }.
  startRsrDay: async (challengeId, { week, day }) => {
    const response = await api.post(`/member/challenges/${challengeId}/rsr/start-day`, { week, day });
    invalidateApiCache([`/member/challenges/${challengeId}/rsr`, '/member/challenges']);
    return response;
  },

  // Start a fresh RSR cycle: archives current maxes to history, sets new maxes,
  // increments the cycle counter. - Mutation
  // body: { squat1RM, bench1RM, deadlift1RM } (kg). Returns the new inputData.
  startNewCycle: async (challengeId, { squat1RM, bench1RM, deadlift1RM }) => {
    const response = await api.post(`/member/challenges/${challengeId}/rsr/new-cycle`, { squat1RM, bench1RM, deadlift1RM });
    invalidateApiCache([`/member/challenges/${challengeId}/rsr`, '/member/challenges']);
    return response;
  },

  // ── Structured meal plans (member side) ──────────────────────────────
  // Assigned plans: full day → meal → item tree + per-day totals + todayDayNumber
  // (repeating cycle from startedAt) + adherence (today's checked mealIds + 7-day %). - Read, short cache
  getMealPlans: () =>
    cachedGet('/member/meal-plans', {
      ttl: 30 * 1000 // 30 seconds
    }),

  // Aggregated shopping list for one plan. `days` = optional window (defaults server-side to
  // one full cycle = dayCount). Returns { items:[{ name, totalGrams|null, count }], days, dayCount }. - Read
  getMealPlanShoppingList: (mealPlanId, days) =>
    api.get(`/member/meal-plans/${mealPlanId}/shopping-list`, {
      params: days != null ? { days } : {}
    }),

  // Adherence check-off — mark today's meal as eaten (idempotent upsert). `optionId` names WHICH
  // alternative was eaten; omit it and the server records the meal's default option. Only one
  // option per meal per day can be checked, so switching option updates the same row. - Mutation
  checkMeal: async (mealPlanId, mealId, optionId = null) => {
    const response = await api.post(
      `/member/meal-plans/${mealPlanId}/meals/${mealId}/check`,
      optionId ? { optionId } : {}
    );
    invalidateApiCache(['/member/meal-plans']);
    return response;
  },

  // Undo today's check-off (idempotent delete). - Mutation
  uncheckMeal: async (mealPlanId, mealId) => {
    const response = await api.delete(`/member/meal-plans/${mealPlanId}/meals/${mealId}/check`);
    invalidateApiCache(['/member/meal-plans']);
    return response;
  },
};

// Plan/Workout API
export const planAPI = {
  // Get plans (role-based) - Cached for 5 minutes
  getPlans: (params = {}) =>
    cachedGet('/plans', {
      params,
      ttl: 5 * 60 * 1000 // 5 minutes
    }),
  
  // Get single plan - Cached for 5 minutes
  getPlan: (planId) =>
    cachedGet(`/plans/${planId}`, {
      ttl: 5 * 60 * 1000 // 5 minutes
    }),

  // Lightweight [{ id, name, exerciseCount }] list of the trainer's REAL plans for
  // pickers (e.g. attaching workouts to a challenge). Generated (RSR/template) plans
  // are excluded server-side. Mirrors exerciseAPI.getAllForPicker. - Cached for 5 minutes
  getPlansForPicker: () =>
    cachedGet('/plans/for-picker', {
      ttl: 5 * 60 * 1000 // 5 minutes
    }),
  
  // Create plan/workout (trainers only) - Invalidate cache
  createPlan: async (planData) => {
    const response = await api.post('/plans', planData);
    invalidateApiCache(['/plans']);
    return response;
  },
  
  // Update plan/workout (trainers only) - Invalidate cache
  updatePlan: async (planId, planData) => {
    const response = await api.put(`/plans/${planId}`, planData);
    invalidateApiCache(['/plans', `/plans/${planId}`]);
    return response;
  },
  
  // Delete plan/workout (trainers only) - Invalidate cache
  deletePlan: async (planId) => {
    const response = await api.delete(`/plans/${planId}`);
    invalidateApiCache(['/plans', `/plans/${planId}`]);
    return response;
  },
};

// Session API
export const sessionAPI = {
  // Get sessions
  getSessions: (params = {}) =>
    api.get('/sessions', { params }),
  
  // Get single session
  getSession: (sessionId) =>
    api.get(`/sessions/${sessionId}`),
  
  // Create session (trainers only)
  createSession: (sessionData) =>
    api.post('/sessions', sessionData),
  
  // Update session
  updateSession: (sessionId, sessionData) =>
    api.put(`/sessions/${sessionId}`, sessionData),
  
  // Delete session (trainers only)
  deleteSession: (sessionId) =>
    api.delete(`/sessions/${sessionId}`),
  
  // Add exercise to session (trainers only)
  addExerciseToSession: (sessionId, exerciseData) =>
    api.post(`/sessions/${sessionId}/exercises`, exerciseData),
  
  // Complete exercise in session (members)
  completeExercise: (sessionId, exerciseId, exerciseData) => {
    const url = `/member/sessions/${sessionId}/exercise/${exerciseId}`;
    return api.put(url, exerciseData);
  },

  // Update session status (members)
  updateSessionStatus: async (sessionId, statusData) => {
    const response = await api.put(`/member/sessions/${sessionId}/status`, statusData);
    // If completing a workout, invalidate caches
    if (statusData.status === 'completed') {
      invalidateApiCache(['/member/sessions', '/member/dashboard']);
    }
    return response;
  },

  // Get session details (members)
  getSessionDetails: (sessionId) =>
    api.get(`/sessions/${sessionId}`),

  // Start session (members) - simplified system
  startSession: (sessionId) =>
    api.put(`/member/sessions/${sessionId}/status`, { 
      status: 'in_progress',
      startTime: new Date().toISOString()
    }),

  // Complete session (members) - simplified system
  completeSession: async (sessionId, workoutData) => {
    const response = await api.put(`/member/sessions/${sessionId}/status`, { 
      status: 'completed',
      globalStopwatchDuration: workoutData.globalStopwatchDuration,
      exercises: workoutData.exercises
    });
    // Invalidate all related caches to ensure fresh data
    invalidateApiCache(['/member/sessions', '/member/dashboard']);
    return response;
  },

  // Get session statistics (previous sessions with same plan)
  getSessionStats: (sessionId) =>
    api.get(`/member/sessions/${sessionId}/stats`),

  // Get current session stats after completion (with comparison)
  getCurrentSessionStats: (sessionId) =>
    api.get(`/member/sessions/${sessionId}/current-stats`),
};

// HIIT Interval Training API
export const hiitAPI = {
  // Get all HIIT workouts (trainer's own or member's trainer's)
  getWorkouts: (params = {}) =>
    cachedGet('/hiit', {
      params,
      ttl: 5 * 60 * 1000 // 5 minutes
    }),
  
  // Get single HIIT workout
  getWorkout: (workoutId) =>
    cachedGet(`/hiit/${workoutId}`, {
      ttl: 5 * 60 * 1000 // 5 minutes
    }),
  
  // Create HIIT workout (trainers only)
  createWorkout: async (workoutData) => {
    const response = await api.post('/hiit', workoutData);
    invalidateApiCache(['/hiit']);
    return response;
  },
  
  // Update HIIT workout (trainers only)
  updateWorkout: async (workoutId, workoutData) => {
    const response = await api.put(`/hiit/${workoutId}`, workoutData);
    invalidateApiCache(['/hiit', `/hiit/${workoutId}`]);
    return response;
  },
  
  // Delete HIIT workout (trainers only)
  deleteWorkout: async (workoutId) => {
    const response = await api.delete(`/hiit/${workoutId}`);
    invalidateApiCache(['/hiit', `/hiit/${workoutId}`]);
    return response;
  },
  
  // Assign HIIT workout to member (trainers only)
  assignWorkout: async (workoutId, assignData) => {
    const response = await api.post(`/hiit/${workoutId}/assign`, assignData);
    invalidateApiCache(['/hiit']);
    return response;
  },
  
  // Get member's HIIT sessions
  getMySessions: (params = {}) =>
    cachedGet('/hiit/sessions/my', {
      params,
      ttl: 30 * 1000 // 30 seconds
    }),
  
  // Get single HIIT session
  getSession: (sessionId) =>
    api.get(`/hiit/sessions/${sessionId}`),
  
  // Start HIIT session from workout (member)
  startSessionFromWorkout: (workoutId) =>
    api.post(`/hiit/sessions/start-from-workout/${workoutId}`),
  
  // Update HIIT session (start, progress, complete)
  updateSession: async (sessionId, sessionData) => {
    const response = await api.put(`/hiit/sessions/${sessionId}`, sessionData);
    if (sessionData.status === 'completed') {
      // Invalidate all relevant caches when HIIT session is completed
      invalidateApiCache([
        '/hiit/sessions/my', 
        '/member/dashboard', 
        '/member/sessions',
        '/member/training-hub'
      ]);
    }
    return response;
  },
  
  // Delete HIIT session (trainers only)
  deleteSession: async (sessionId) => {
    const response = await api.delete(`/hiit/sessions/${sessionId}`);
    invalidateApiCache(['/hiit']);
    return response;
  }
};

// Training Programs API (multi-session workout plans)
export const programAPI = {
  // Get all training programs (trainer's own)
  getPrograms: (params = {}) =>
    cachedGet('/programs', {
      params,
      ttl: 5 * 60 * 1000 // 5 minutes
    }),
  
  // Get single training program with full details
  getProgram: (programId) =>
    cachedGet(`/programs/${programId}`, {
      ttl: 5 * 60 * 1000 // 5 minutes
    }),
  
  // Create training program (trainers only)
  createProgram: async (programData) => {
    const response = await api.post('/programs', programData);
    invalidateApiCache(['/programs']);
    return response;
  },
  
  // Update training program (trainers only)
  updateProgram: async (programId, programData) => {
    const response = await api.put(`/programs/${programId}`, programData);
    invalidateApiCache(['/programs', `/programs/${programId}`]);
    return response;
  },
  
  // Delete training program (trainers only)
  deleteProgram: async (programId) => {
    const response = await api.delete(`/programs/${programId}`);
    invalidateApiCache(['/programs', `/programs/${programId}`]);
    return response;
  },
  
  // Enroll member in program (trainers only)
  enrollMember: async (programId, enrollData) => {
    const response = await api.post(`/programs/${programId}/enroll`, enrollData);
    invalidateApiCache(['/programs', `/programs/${programId}`]);
    return response;
  },
  
  // Remove member from program (trainers only)
  removeMember: async (programId, memberId) => {
    const response = await api.delete(`/programs/${programId}/enroll/${memberId}`);
    invalidateApiCache(['/programs', `/programs/${programId}`]);
    return response;
  },
  
  // Update enrollment status (trainers only)
  updateEnrollment: async (enrollmentId, enrollmentData) => {
    const response = await api.put(`/programs/enrollment/${enrollmentId}`, enrollmentData);
    invalidateApiCache(['/programs']);
    return response;
  },
  
  // Get member's enrolled programs
  getMyEnrollments: (params = {}) =>
    cachedGet('/programs/member/enrolled', {
      params,
      ttl: 30 * 1000 // 30 seconds
    }),
  
  // Get program schedule for member
  getMySchedule: (enrollmentId, params = {}) =>
    cachedGet(`/programs/member/${enrollmentId}/schedule`, {
      params,
      ttl: 30 * 1000 // 30 seconds
    }),
  
  // Update member's program progress
  updateProgress: async (enrollmentId, progressData) => {
    const response = await api.put(`/programs/member/${enrollmentId}/progress`, progressData);
    invalidateApiCache(['/programs/member/enrolled']);
    return response;
  },
  
  // Get single enrollment with full program details (for member viewing their enrolled program)
  getEnrollment: (enrollmentId) =>
    cachedGet(`/programs/member/${enrollmentId}`, {
      ttl: 30 * 1000 // 30 seconds
    }),
  
  // Start a workout from a program item
  startProgramWorkout: async (enrollmentId, itemId) => {
    const response = await api.post(`/programs/member/${enrollmentId}/start-workout`, { itemId });
    invalidateApiCache([`/programs/member/${enrollmentId}`]);
    return response;
  },
  
  // Get the completed session for a specific program item
  getCompletedSession: (enrollmentId, itemId) =>
    api.get(`/programs/member/${enrollmentId}/completed-session/${itemId}`),

  // List the built-in routine templates (Arnold/Jay/Calum) the trainer can seed from.
  // Returns { templates: [{ key, name, dayCount, days:[{ name, exerciseCount }] }] }.
  getRoutineTemplates: () =>
    api.get('/programs/routine-templates'),

  // Seed a real, editable TrainingProgram from a routine template. - Mutation
  // body: { routineKey, name? }. Returns { program } (201).
  createProgramFromRoutine: async ({ routineKey, name } = {}) => {
    const response = await api.post('/programs/from-routine', { routineKey, name });
    invalidateApiCache(['/programs']);
    return response;
  },
};

// Progress Photos API
export const progressPhotoAPI = {
  // Get all progress photos
  getProgressPhotos: () => api.get('/progress-photos'),

  // Upload new progress photo
  uploadProgressPhoto: (formData) => 
    api.post('/progress-photos', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // Update progress photo details
  updateProgressPhoto: (photoId, photoData) =>
    api.put(`/progress-photos/${photoId}`, photoData),

  // Delete progress photo
  deleteProgressPhoto: (photoId) =>
    api.delete(`/progress-photos/${photoId}`),
};

// Public API (no auth required)
export const publicAPI = {
  getFavicon: () => api.get('/public/favicon'),
  getLogo: () => api.get('/public/logo'),
  // Trainer's public WhatsApp number for the shop FAB (no auth; null when unset)
  getPublicWhatsapp: () => api.get('/shop/public/whatsapp'),
};

// Avatar API - interceptor already handles FormData (removes Content-Type for boundary)
export const avatarAPI = {
  uploadMemberAvatar: (formData) => api.post('/avatar/member', formData),

  uploadTrainerAvatar: (formData) => api.post('/avatar/trainer', formData),

  uploadAdministratorAvatar: (formData) => api.post('/avatar/administrator', formData),

  // Delete member avatar
  deleteMemberAvatar: () =>
    api.delete('/avatar/member'),

  // Delete trainer avatar
  deleteTrainerAvatar: () =>
    api.delete('/avatar/trainer'),

  // Delete administrator avatar
  deleteAdministratorAvatar: () =>
    api.delete('/avatar/administrator'),
};

// Chat API
export const chatAPI = {
  // Get all chats for current user
  getChats: () =>
    api.get('/chat'),

  // Get chat with messages
  getChat: (chatId, params = {}) =>
    api.get(`/chat/${chatId}`, { params }),

  // Get or create chat with specific user
  getChatWith: (userType, userId) =>
    api.get(`/chat/with/${userType}/${userId}`),

  // Send message (will be handled by socket.io in real implementation)
  sendMessage: (chatId, messageData) =>
    api.post(`/chat/${chatId}/messages`, messageData),

  // Upload attachment for chat
  uploadAttachment: (chatId, formData) =>
    api.post(`/chat/${chatId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // Get unread message count
  getUnreadCount: () =>
    api.get('/chat/unread-count'),

  // Get list of unread messages with sender info
  getUnreadMessages: () =>
    api.get('/chat/unread-messages'),

  // Mark all messages in a chat as read
  markChatAsRead: (chatId) =>
    api.post(`/chat/${chatId}/mark-read`),

  // Send bulk message to multiple members (trainers only)
  sendBulkMessage: (memberIds, content, attachmentUrl = null) =>
    api.post('/chat/bulk-message', { memberIds, content, attachmentUrl }),

  // Get all chats with members for inbox view (trainers only)
  getAllChatsWithMembers: () =>
    api.get('/chat/all-chats-with-members'),

  // Upload attachment for bulk messages (trainers only)
  uploadBulkAttachment: (formData) =>
    api.post('/chat/upload-attachment', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
};

// Notifications API - server-backed notification inbox.
// Intentionally uncached: the badge count and list must always be fresh.
export const notificationsAPI = {
  // List notifications (createdAt desc). Cursor pagination: pass nextCursor to
  // load the next page. Returns { notifications, nextCursor }.
  getNotifications: ({ cursor, limit = 20 } = {}) =>
    api.get('/notifications', { params: { cursor, limit } }),

  // Unread badge count: { unreadCount }
  getUnreadCount: () =>
    api.get('/notifications/unread-count'),

  // Mark a single notification as read
  markRead: (id) =>
    api.post(`/notifications/${id}/read`),

  // Mark all of the caller's notifications as read
  markAllRead: () =>
    api.post('/notifications/read-all'),

  // Delete a single notification
  remove: (id) =>
    api.delete(`/notifications/${id}`),
};

export default api;
